import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const navLinkClass = ({ isActive }) => 
    isActive 
      ? "bg-primary-700 text-white px-3 py-2 rounded-md text-sm font-medium" 
      : "text-gray-300 hover:bg-primary-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium";

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Navigation */}
      <nav className="bg-primary-800">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-white text-xl font-bold">Options Backtesting</h1>
              </div>
              <div className="hidden md:block">
                <div className="ml-10 flex items-baseline space-x-4">
                  <NavLink to="/" className={navLinkClass} end>Dashboard</NavLink>
                  <NavLink to="/backtest" className={navLinkClass}>Backtesting</NavLink>
                  <NavLink to="/option-chain" className={navLinkClass}>Option Chain</NavLink>
                  <NavLink to="/data" className={navLinkClass}>Data</NavLink>
                  <NavLink to="/settings" className={navLinkClass}>Settings</NavLink>
                </div>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="ml-4 flex items-center md:ml-6">
                <div className="ml-3 relative">
                  <div className="flex items-center">
                    <span className="text-gray-300 text-sm mr-4">
                      Welcome, {user?.username}
                    </span>
                    <button
                      onClick={handleLogout}
                      className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="-mr-2 flex md:hidden">
              <button
                onClick={toggleMobileMenu}
                type="button"
                className="bg-primary-900 inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                aria-controls="mobile-menu"
                aria-expanded="false"
              >
                <span className="sr-only">Open main menu</span>
                {!isMobileMenuOpen ? (
                  <svg
                    className="block h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                ) : (
                  <svg
                    className="block h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`${isMobileMenuOpen ? 'block' : 'hidden'} md:hidden`}
          id="mobile-menu"
        >
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <NavLink 
              to="/" 
              className={({ isActive }) => 
                isActive 
                  ? "bg-primary-900 text-white block px-3 py-2 rounded-md text-base font-medium" 
                  : "text-gray-300 hover:bg-primary-700 hover:text-white block px-3 py-2 rounded-md text-base font-medium"
              }
              end
            >
              Dashboard
            </NavLink>
            <NavLink 
              to="/backtest" 
              className={({ isActive }) => 
                isActive 
                  ? "bg-primary-900 text-white block px-3 py-2 rounded-md text-base font-medium" 
                  : "text-gray-300 hover:bg-primary-700 hover:text-white block px-3 py-2 rounded-md text-base font-medium"
              }
            >
              Backtesting
            </NavLink>
            <NavLink 
              to="/option-chain" 
              className={({ isActive }) => 
                isActive 
                  ? "bg-primary-900 text-white block px-3 py-2 rounded-md text-base font-medium" 
                  : "text-gray-300 hover:bg-primary-700 hover:text-white block px-3 py-2 rounded-md text-base font-medium"
              }
            >
              Option Chain
            </NavLink>
            <NavLink 
              to="/data" 
              className={({ isActive }) => 
                isActive 
                  ? "bg-primary-900 text-white block px-3 py-2 rounded-md text-base font-medium" 
                  : "text-gray-300 hover:bg-primary-700 hover:text-white block px-3 py-2 rounded-md text-base font-medium"
              }
            >
              Data
            </NavLink>
            <NavLink 
              to="/settings" 
              className={({ isActive }) => 
                isActive 
                  ? "bg-primary-900 text-white block px-3 py-2 rounded-md text-base font-medium" 
                  : "text-gray-300 hover:bg-primary-700 hover:text-white block px-3 py-2 rounded-md text-base font-medium"
              }
            >
              Settings
            </NavLink>
          </div>
          <div className="pt-4 pb-3 border-t border-primary-700">
            <div className="flex items-center px-5">
              <div className="ml-3">
                <div className="text-base font-medium leading-none text-white">
                  {user?.username}
                </div>
                <div className="text-sm font-medium leading-none text-gray-400">
                  {user?.email}
                </div>
              </div>
            </div>
            <div className="mt-3 px-2 space-y-1">
              <button
                onClick={handleLogout}
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-400 hover:text-white hover:bg-primary-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main>
        <div className="max-w-8xl mx-auto py-6 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
